import React from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Backdrop } from "@mui/material";
import GeneralDialog from "src/components/GeneralDialog";

const useStyles = makeStyles((theme) => ({
  scrollContainer: {
    maxHeight: "70vh",
    overflowY: "auto",
    backgroundColor: "#f3f3f3",
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  nodeSelect: {
    minWidth: 300,
    backgroundColor: "#ffffff",
    marginRight: theme.spacing(2),
  },
  rangeInput: {
    minWidth: 250,
    backgroundColor: "#ffffff",
    marginRight: theme.spacing(2),
  },
  backdrop: {
    zIndex: 1500,
    color: "#fff",
  },
}));

export default function NodeReassignPanelComponent({
  nodes,
  sourceNodeUuid,
  targetNodeUuid,
  mode,
  fromValue,
  toValue,
  isSubmitting,
  isSubmitDisabled,
  showConfirmDialog,
  setSourceNodeUuid,
  setTargetNodeUuid,
  handleModeChange,
  setFromValue,
  setToValue,
  handleSubmit,
  handleConfirmReassign,
  handleCancelDialog,
  onSerialNumberAssignDialogCloseClick,
}) {
  const classes = useStyles();

  const targetNodes = nodes.filter((n) => n.uuid !== sourceNodeUuid);

  return (
    <>
      <DialogContent dividers className={classes.scrollContainer}>
        {/* Node Selection */}
        <Box className={classes.section}>
          <Typography variant="body2" style={{ marginBottom: 12, fontWeight: 500 }}>
            Select Nodes
          </Typography>
          <Box display="flex" flexWrap="wrap">
            <FormControl variant="outlined" className={classes.nodeSelect}>
              <InputLabel>Source Node</InputLabel>
              <Select
                value={sourceNodeUuid}
                onChange={(e) => {
                  setSourceNodeUuid(e.target.value);
                  if (e.target.value === targetNodeUuid) {
                    setTargetNodeUuid("");
                  }
                }}
                label="Source Node"
                disabled={isSubmitting}
              >
                {nodes.map((node) => (
                  <MenuItem key={node.uuid} value={node.uuid}>
                    {node.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant="outlined" className={classes.nodeSelect}>
              <InputLabel>Target Node</InputLabel>
              <Select
                value={targetNodeUuid}
                onChange={(e) => setTargetNodeUuid(e.target.value)}
                label="Target Node"
                disabled={isSubmitting || !sourceNodeUuid}
              >
                {targetNodes.map((node) => (
                  <MenuItem key={node.uuid} value={node.uuid}>
                    {node.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Mode Selection */}
        <Box className={classes.section}>
          <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 500 }}>
            Range Type
          </Typography>
          <RadioGroup row value={String(mode)} onChange={handleModeChange}>
            <FormControlLabel
              value="1"
              control={<Radio color="primary" />}
              label="Sequence Number"
              disabled={isSubmitting}
            />
            <FormControlLabel
              value="2"
              control={<Radio color="primary" />}
              label="Serial Number"
              disabled={isSubmitting}
            />
          </RadioGroup>
        </Box>

        {/* Range Input */}
        <Box className={classes.section}>
          <Typography variant="body2" style={{ marginBottom: 12, fontWeight: 500 }}>
            {mode === 1 ? "Sequence Number Range" : "Serial Number Range"}
          </Typography>
          <Box display="flex" flexWrap="wrap">
            <TextField
              variant="outlined"
              label="From"
              value={fromValue}
              onChange={(e) => setFromValue(e.target.value)}
              className={classes.rangeInput}
              type={mode === 1 ? "number" : "text"}
              disabled={isSubmitting}
            />
            <TextField
              variant="outlined"
              label="To"
              value={toValue}
              onChange={(e) => setToValue(e.target.value)}
              className={classes.rangeInput}
              type={mode === 1 ? "number" : "text"}
              disabled={isSubmitting}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="text"
          type="button"
          disabled={isSubmitting}
          onClick={onSerialNumberAssignDialogCloseClick}
          style={{ color: "black" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          type="button"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          Reassign
        </Button>
      </DialogActions>

      <GeneralDialog
        isOpen={showConfirmDialog}
        handleClose={handleCancelDialog}
        handleProceed={handleConfirmReassign}
        title="Reassign Serial Number Node"
        description="Are you sure you want to reassign the serial numbers to the target node? This action updates the node_id for all serial numbers in the specified range."
        type="danger"
      />

      <Backdrop open={isSubmitting} className={classes.backdrop}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <Box>
            <CircularProgress color="inherit" />
          </Box>
          <Box pt={1}>
            <Typography>Reassigning...</Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
}
